import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Switch,
    Slider,
    FormControlLabel,
    Divider,
    Grid,
    IconButton,
    Button,
    useTheme,
    alpha,
    Stack,
    Tooltip,
    Alert
} from '@mui/material';
import {
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Speed as SpeedIcon,
    Memory as MemoryIcon,
    Security as SecurityIcon,
    Save as SaveIcon,
    RestartAlt as RestartAltIcon,
    BugReport as BugReportIcon,
    Language as LanguageIcon
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';

const SettingsView = () => {
    const theme = useTheme();
    const { mode, toggleTheme } = useCustomTheme();
    const [success, setSuccess] = useState(false);
    
    // Impostazioni di scansione
    const [scanSettings, setScanSettings] = useState({
        maxThreads: 10,
        crawlerThreads: 5,
        requestTimeout: 30,
        maxDepth: 5,
        followRedirects: true,
        detectWAF: true,
        passiveScan: true,
        aggressiveScan: false
    });

    // Impostazioni tema
    const [themeSettings, setThemeSettings] = useState({
        darkMode: mode === 'dark',
        animations: true,
        compactMode: false,
        highContrast: false
    });

    // Carica le impostazioni dal localStorage
    useEffect(() => {
        const savedScanSettings = localStorage.getItem('scanSettings');
        const savedThemeSettings = localStorage.getItem('themeSettings');

        if (savedScanSettings) {
            setScanSettings(JSON.parse(savedScanSettings));
        }
        if (savedThemeSettings) {
            setThemeSettings(JSON.parse(savedThemeSettings));
        }
    }, []);

    // Salva le impostazioni nel localStorage
    const handleSave = () => {
        localStorage.setItem('scanSettings', JSON.stringify(scanSettings));
        localStorage.setItem('themeSettings', JSON.stringify(themeSettings));
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
    };

    // Reset delle impostazioni
    const handleReset = () => {
        setScanSettings({
            maxThreads: 10,
            crawlerThreads: 5,
            requestTimeout: 30,
            maxDepth: 5,
            followRedirects: true,
            detectWAF: true,
            passiveScan: true,
            aggressiveScan: false
        });
        setThemeSettings({
            darkMode: false,
            animations: true,
            compactMode: false,
            highContrast: false
        });
        localStorage.removeItem('scanSettings');
        localStorage.removeItem('themeSettings');
    };

    // Gestione del cambio tema
    const handleThemeChange = () => {
        setThemeSettings(prev => ({
            ...prev,
            darkMode: !prev.darkMode
        }));
        toggleTheme();
    };

    return (
        <Box 
            sx={{ 
                p: 4,
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
            }}
        >
            <Typography 
                variant="h4" 
                sx={{ 
                    mb: 4,
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: 'center'
                }}
            >
                Settings
            </Typography>

            {success && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1)
                    }}
                >
                    Settings saved successfully
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Theme Settings */}
                <Grid item xs={12} md={6}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            p: 3,
                            height: '100%',
                            bgcolor: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 4
                        }}
                    >
                        <Stack spacing={3}>
                            <Typography variant="h6" sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                color: theme.palette.primary.main
                            }}>
                                <LightModeIcon /> Theme Settings
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={themeSettings.darkMode}
                                        onChange={handleThemeChange}
                                        color="primary"
                                    />
                                }
                                label="Dark Mode"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={themeSettings.animations}
                                        onChange={(e) => setThemeSettings(prev => ({
                                            ...prev,
                                            animations: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Animations"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={themeSettings.compactMode}
                                        onChange={(e) => setThemeSettings(prev => ({
                                            ...prev,
                                            compactMode: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Compact Mode"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={themeSettings.highContrast}
                                        onChange={(e) => setThemeSettings(prev => ({
                                            ...prev,
                                            highContrast: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="High Contrast"
                            />
                        </Stack>
                    </Card>
                </Grid>

                {/* Scanner Settings */}
                <Grid item xs={12} md={6}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            p: 3,
                            height: '100%',
                            bgcolor: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 4
                        }}
                    >
                        <Stack spacing={3}>
                            <Typography variant="h6" sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                color: theme.palette.primary.main
                            }}>
                                <BugReportIcon /> Scanner Settings
                            </Typography>

                            <Box>
                                <Typography gutterBottom>
                                    Scanner Threads ({scanSettings.maxThreads})
                                </Typography>
                                <Slider
                                    value={scanSettings.maxThreads}
                                    onChange={(e, value) => setScanSettings(prev => ({
                                        ...prev,
                                        maxThreads: value
                                    }))}
                                    min={1}
                                    max={20}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                            </Box>

                            <Box>
                                <Typography gutterBottom>
                                    Crawler Threads ({scanSettings.crawlerThreads})
                                </Typography>
                                <Slider
                                    value={scanSettings.crawlerThreads}
                                    onChange={(e, value) => setScanSettings(prev => ({
                                        ...prev,
                                        crawlerThreads: value
                                    }))}
                                    min={1}
                                    max={10}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                            </Box>

                            <Box>
                                <Typography gutterBottom>
                                    Request Timeout (seconds) ({scanSettings.requestTimeout})
                                </Typography>
                                <Slider
                                    value={scanSettings.requestTimeout}
                                    onChange={(e, value) => setScanSettings(prev => ({
                                        ...prev,
                                        requestTimeout: value
                                    }))}
                                    min={5}
                                    max={60}
                                    step={5}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                            </Box>

                            <Box>
                                <Typography gutterBottom>
                                    Maximum Depth ({scanSettings.maxDepth})
                                </Typography>
                                <Slider
                                    value={scanSettings.maxDepth}
                                    onChange={(e, value) => setScanSettings(prev => ({
                                        ...prev,
                                        maxDepth: value
                                    }))}
                                    min={1}
                                    max={10}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={scanSettings.followRedirects}
                                        onChange={(e) => setScanSettings(prev => ({
                                            ...prev,
                                            followRedirects: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Follow Redirects"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={scanSettings.detectWAF}
                                        onChange={(e) => setScanSettings(prev => ({
                                            ...prev,
                                            detectWAF: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Detect WAF"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={scanSettings.passiveScan}
                                        onChange={(e) => setScanSettings(prev => ({
                                            ...prev,
                                            passiveScan: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Passive Scan"
                            />

                            <FormControlLabel
                                control={
                                    <Switch 
                                        checked={scanSettings.aggressiveScan}
                                        onChange={(e) => setScanSettings(prev => ({
                                            ...prev,
                                            aggressiveScan: e.target.checked
                                        }))}
                                        color="primary"
                                    />
                                }
                                label="Aggressive Scan"
                            />
                        </Stack>
                    </Card>
                </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                gap: 2,
                justifyContent: 'center'
            }}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltIcon />}
                    sx={{ 
                        borderRadius: 2,
                        px: 4
                    }}
                >
                    Reset to Default
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    sx={{ 
                        borderRadius: 2,
                        px: 4,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                        '&:hover': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                        }
                    }}
                >
                    Save Settings
                </Button>
            </Box>
        </Box>
    );
};

export default SettingsView;
